import { Alert, Flex, Spin, Empty, Pagination, Modal, Input } from 'antd';
import styles from './redirection-targets.module.scss';
import { CompanySelectBh } from '../company-select-bh/company-select-bh';
import { useSelectedCompany } from '@/hooks/use-selected-company/use-selected-company';
import { useCompaniesList } from '@/hooks/use-company/use-company';
import { useCompanyStore } from '@/store/app.store';
import { Button } from '../button/button';
import Card from '@/components/card/card';
import IconButton from '@/components/icon-button/icon-button';
import Icon, { LoadingOutlined } from '@ant-design/icons';
import Search from '@/assets/icons/search-md.svg?react';
import PlusIcon from '@/assets/icons/plus.svg?react';
import EditIcon from '@/assets/icons/pencil-solid-full.svg?react';
import TrashIcon from '@/assets/icons/trash-01.svg?react';
import Arrow from '@/assets/icons/chevron-right.svg?react';
import ArrowLeft from '@/assets/icons/chevron-left.svg?react';
import { useState, useMemo, useEffect, useCallback } from 'react';
import { debounce } from '@/utils/debounce/debounce';

const SEARCH_DELAY = 500;
import {
  useRedirectionTargets,
  useCreateRedirectionTarget,
  useUpdateRedirectionTarget,
  useDeleteRedirectionTarget,
} from '@/hooks/use-redirection-targets/use-redirection-targets';
import {
  RedirectionTargetModal,
  RedirectionTargetFormValues,
} from '../redirection-target-modal/redirection-target-modal';
import { RedirectionTarget } from '@/services/redirection-targets-service/redirection-targets-service.types';
import { useTranslation } from 'react-i18next';

export function RedirectionTargets() {
  const { t } = useTranslation(['settings', 'common']);
  const { selectedCompany, setSelectedCompany } = useSelectedCompany({
    storageKey: 'selectedCompany_company',
  });
  const { companyName, setCompanyName } = useCompanyStore();
  const { companies } = useCompaniesList();

  useEffect(() => {
    if (companyName && companies) {
      const company = companies.find((c) => c.name === companyName);
      if (company) {
        setSelectedCompany((prev) =>
          prev?.id === company.id
            ? prev
            : { id: company.id, name: company.name, time_zone: company.time_zone }
        );
      }
    }
  }, [companyName, companies]);

  const handleCompanyChange = useCallback(
    (company: typeof selectedCompany) => {
      setSelectedCompany(company);
      setCompanyName(company?.name);
    },
    [setSelectedCompany, setCompanyName]
  );

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
  const [selectedTarget, setSelectedTarget] = useState<RedirectionTarget | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [targetToDelete, setTargetToDelete] = useState<number | null>(null);
  const [filterValue, setFilterValue] = useState('');

  const { redirectionTargets, totalCount, isLoading, error } = useRedirectionTargets(
    selectedCompany?.id
      ? {
          company_id: selectedCompany.id,
          page: currentPage,
          items_per_page: pageSize,
          search: filterValue || undefined,
        }
      : undefined
  );

  const { createRedirectionTargetAsync, isPending: isCreating } = useCreateRedirectionTarget();
  const { updateRedirectionTargetAsync, isPending: isUpdating } = useUpdateRedirectionTarget();
  const { deleteRedirectionTarget, isPending: isDeleting } = useDeleteRedirectionTarget();

  const debouncedSearch = useMemo(
    () =>
      debounce((value: string) => {
        setFilterValue(value);
        setCurrentPage(1);
      }, SEARCH_DELAY),
    []
  );

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    debouncedSearch(value);
  };

  const handleOpenCreateModal = () => {
    setModalMode('create');
    setSelectedTarget(null);
    setModalOpen(true);
  };

  const handleOpenEditModal = (target: RedirectionTarget) => {
    setModalMode('edit');
    setSelectedTarget(target);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedTarget(null);
  };

  // Rejects on a failed save; the modal reads the API error off the rejection.
  const handleSave = async (values: RedirectionTargetFormValues) => {
    if (!selectedCompany) return;

    const redirection_target = {
      name: values.name,
      phone: values.phone,
      after_hours_phone: values.after_hours_phone,
      aliases: values.aliases,
      email: values.email,
      extension: values.extension,
    };

    if (modalMode === 'create') {
      await createRedirectionTargetAsync({
        companyId: selectedCompany.id,
        payload: { redirection_target },
      });
    } else if (modalMode === 'edit' && selectedTarget) {
      await updateRedirectionTargetAsync({
        id: selectedTarget.id,
        companyId: selectedCompany.id,
        payload: { redirection_target },
      });
    }
    handleCloseModal();
  };

  const handleOpenDeleteModal = (id: number) => {
    setTargetToDelete(id);
    setDeleteModalOpen(true);
  };

  const handleCloseDeleteModal = () => {
    setDeleteModalOpen(false);
    setTargetToDelete(null);
  };

  const handleConfirmDelete = () => {
    if (targetToDelete && selectedCompany) {
      deleteRedirectionTarget(
        { id: targetToDelete, companyId: selectedCompany.id },
        {
          onSuccess: () => {
            handleCloseDeleteModal();

            const remainingItems = totalCount - 1;
            const maxPage = Math.ceil(remainingItems / pageSize);

            if (currentPage > maxPage && maxPage > 0) {
              setCurrentPage(maxPage);
            } else if (remainingItems === 0) {
              setCurrentPage(1);
            }
          },
        }
      );
    }
  };

  return (
    <Flex vertical gap={16}>
      <CompanySelectBh value={selectedCompany} onChange={handleCompanyChange} />

      {selectedCompany && (
        <>
          <Button
            buttonVariant="primary"
            onClick={handleOpenCreateModal}
            className={styles.addButton}
          >
            <Icon component={() => <PlusIcon className={styles.plusIcon} />} />
            {t('settings:redirectionTargets.add')}
          </Button>

          <Flex
            justify="space-between"
            align="center"
            gap={16}
            wrap="wrap"
            style={{ marginBottom: 16 }}
          >
            <Flex align="center" gap={8}>
              <span className={styles.targetCountLabel}>
                {t('settings:redirectionTargets.count')}
              </span>
              <span className={styles.targetCountValue}>{totalCount}</span>
            </Flex>
            <Flex vertical gap={5} className={styles.searchContainer}>
              <Input
                onChange={handleSearchChange}
                placeholder={t('common:actions.search')}
                prefix={
                  <Search stroke={styles.iconColor} height={16} width={16} strokeWidth={2.5} />
                }
              />
            </Flex>
          </Flex>

          {isLoading ? (
            <div className={styles.loadingWrapper}>
              <Spin indicator={<LoadingOutlined spin />} />
            </div>
          ) : error ? (
            <Alert
              message={t('settings:redirectionTargets.loadError.title')}
              description={t('settings:redirectionTargets.loadError.description')}
              type="error"
              showIcon
            />
          ) : redirectionTargets.length === 0 ? (
            <Empty description={t('settings:redirectionTargets.empty')} />
          ) : (
            <>
              <Card hint={t('settings:redirectionTargets.hint')}>
                {redirectionTargets.map((target) => (
                  <div className={styles.row} key={target.id}>
                    <div className={styles.rowText}>
                      <h4 className={styles.rowName}>{target.name}</h4>
                      <p className={styles.rowInfo}>
                        <span className={styles.label}>
                          {t('settings:redirectionTargets.row.phone')}
                        </span>{' '}
                        {target.phone}
                      </p>
                      {target.after_hours_phone && (
                        <p className={styles.rowInfo}>
                          <span className={styles.label}>
                            {t('settings:redirectionTargets.row.afterHoursPhone')}
                          </span>{' '}
                          {target.after_hours_phone}
                        </p>
                      )}
                      {target.aliases && (
                        <p className={styles.rowInfo}>
                          <span className={styles.label}>
                            {t('settings:redirectionTargets.row.aliases')}
                          </span>{' '}
                          {target.aliases}
                        </p>
                      )}
                      {target.email && (
                        <p className={styles.rowInfo}>
                          <span className={styles.label}>
                            {t('settings:redirectionTargets.row.email')}
                          </span>{' '}
                          {target.email}
                        </p>
                      )}
                      {target.extension && (
                        <p className={styles.rowInfo}>
                          <span className={styles.label}>
                            {t('settings:redirectionTargets.row.extension')}
                          </span>{' '}
                          {target.extension}
                        </p>
                      )}
                    </div>
                    <Flex gap={8}>
                      {/* Phone joins the label because name alone is not unique per company. */}
                      <IconButton
                        label={t('settings:redirectionTargets.editLabel', {
                          name: target.name,
                          phone: target.phone,
                        })}
                        onClick={() => handleOpenEditModal(target)}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        label={t('settings:redirectionTargets.deleteLabel', {
                          name: target.name,
                          phone: target.phone,
                        })}
                        loading={isDeleting && targetToDelete === target.id}
                        onClick={() => handleOpenDeleteModal(target.id)}
                      >
                        <TrashIcon />
                      </IconButton>
                    </Flex>
                  </div>
                ))}
              </Card>

              {totalCount > 10 && (
                <Flex justify="center" style={{ marginTop: 24 }}>
                  <Pagination
                    className={styles.pagination}
                    current={currentPage}
                    pageSize={pageSize}
                    total={totalCount}
                    onChange={(page, size) => {
                      setCurrentPage(page);
                      setPageSize(size);
                    }}
                    pageSizeOptions={['10', '25', '50']}
                    showSizeChanger
                    locale={{ items_per_page: '' }}
                    nextIcon={
                      <Icon component={() => <Arrow strokeWidth={1} width={16} height={16} />} />
                    }
                    prevIcon={
                      <Icon
                        component={() => <ArrowLeft strokeWidth={2} width={16} height={16} />}
                      />
                    }
                  />
                </Flex>
              )}
            </>
          )}

          <RedirectionTargetModal
            open={modalOpen}
            onClose={handleCloseModal}
            onSave={handleSave}
            isLoading={isCreating || isUpdating}
            initialValues={selectedTarget}
            mode={modalMode}
          />

          <Modal
            title={t('settings:redirectionTargets.deleteModal.title')}
            open={deleteModalOpen}
            onCancel={handleCloseDeleteModal}
            onOk={handleConfirmDelete}
            okText={t('common:actions.delete')}
            cancelText={t('common:actions.cancel')}
            okButtonProps={{ danger: true, loading: isDeleting }}
          >
            <p>{t('settings:redirectionTargets.deleteModal.content')}</p>
          </Modal>
        </>
      )}
    </Flex>
  );
}
